import React from 'react';
import { cn } from '@/lib/utils';
import { Button } from './button';
import { motion } from 'framer-motion';
import { CheckCircle, Clock, XCircle, Lightbulb, Brain, MessageSquare, X } from 'lucide-react';

// Session summary data interface
export interface SessionSummaryData {
  keyChallenge?: string;
  discussionInsights?: string[];
  behaviouralAnalysis?: {
    language?: string;
    emotions?: string;
    body?: string;
    mindsets?: string;
  };
  title?: string;
  completionStatus?: 'completed' | 'incomplete' | 'failed';
}

interface SessionSummaryProps {
  data: SessionSummaryData;
  className?: string;
  onClose?: () => void;
}

/**
 * Displays a summary of the coaching session with insights and analysis
 */
export function SessionSummary({ data, className, onClose }: SessionSummaryProps) {
  // Check if this is a generating summary
  const isGenerating = data.title === 'Generating Your Session Summary...' ||
                      data.keyChallenge === "Thank you for your session. The coach is analyzing your conversation.";

  const getStatusIcon = () => {
    switch (data.completionStatus) {
      case 'completed':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'incomplete':
        return <Clock className="h-5 w-5 text-yellow-500" />;
      case 'failed':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return null;
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      className={cn('bg-white rounded-xl border border-gray-200 overflow-hidden max-w-4xl mx-auto', className)}
    >
      {/* Enhanced Header */}
      <div className="bg-gray-50 px-6 py-5 border-b border-gray-200">
        <div className="flex justify-between items-start">
          <div className="flex items-start space-x-3">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2 }}
              className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center"
            >
              <Brain className="h-6 w-6 text-white" />
            </motion.div>
            <div>
              <motion.h2 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-xl sm:text-2xl font-semibold text-gray-900"
              >
                {isGenerating ? 'Generating Your Session Summary...' : (data.title || 'Session Summary')}
              </motion.h2>
              {data.completionStatus && (
                <motion.div
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                  className="flex items-center mt-1 space-x-2"
                >
                  {getStatusIcon()}
                  <span
                    className={cn(
                      'px-3 py-1 text-xs font-medium rounded-full',
                      data.completionStatus === 'completed' && 'bg-green-100 text-green-700',
                      data.completionStatus === 'incomplete' && 'bg-yellow-100 text-yellow-700',
                      data.completionStatus === 'failed' && 'bg-red-100 text-red-700'
                    )}
                  >
                    {data.completionStatus}
                  </span>
                </motion.div>
              )}
            </div>
          </div>

          {onClose && (
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors text-gray-600 hover:text-gray-800"
              aria-label="Close summary"
            >
              <X className="h-5 w-5" />
            </motion.button>
          )}
        </div>
      </div>

      {/* Content Area */}
      <div className="p-6 max-h-[70vh] overflow-y-auto">

        {/* Enhanced loading indication */}
        {isGenerating && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col items-center justify-center py-12"
          >
            <motion.div 
              className="flex space-x-2 mb-6"
            >
              {[0, 1, 2].map((i) => (
                <motion.div
                  key={i}
                  className="h-3 w-3 bg-primary rounded-full"
                  animate={{
                    scale: [1, 1.3, 1],
                    opacity: [0.5, 1, 0.5]
                  }}
                  transition={{
                    duration: 0.8,
                    repeat: Infinity,
                    delay: i * 0.2
                  }}
                />
              ))}
            </motion.div>
            <p className="text-center text-gray-600 font-medium">
              AI Coach is analyzing your session...
            </p>
            <p className="text-center text-gray-500 text-sm mt-2">
              This will take just a moment
            </p>
          </motion.div>
        )}

        {/* Enhanced Key Challenge Section */}
        {!isGenerating && data.keyChallenge && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8"
          >
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-center mb-3">
                <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center mr-3">
                  <Lightbulb className="h-4 w-4 text-white" />
                </div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                  Key Challenge
                </h3>
              </div>
              <p className="text-gray-800 leading-relaxed">{data.keyChallenge}</p>
            </div>
          </motion.div>
        )}

        {/* Enhanced Discussion Insights Section */}
        {!isGenerating && data.discussionInsights && data.discussionInsights.length > 0 && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-8"
          >
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-gray-600 rounded-lg flex items-center justify-center mr-3">
                  <MessageSquare className="h-4 w-4 text-white" />
                </div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                  Discussion Insights
                </h3>
              </div>
              <div className="space-y-3">
                {data.discussionInsights.map((insight, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className="flex items-start space-x-3"
                  >
                    <div className="w-2 h-2 bg-gray-400 rounded-full mt-2 flex-shrink-0" />
                    <p className="text-gray-800 leading-relaxed">{insight}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {/* Enhanced Behavioural Analysis Section */}
        {!isGenerating && data.behaviouralAnalysis && (
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-gray-600 rounded-lg flex items-center justify-center mr-3">
                  <Brain className="h-4 w-4 text-white" />
                </div>
                <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">
                  Behavioural Analysis
                </h3>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {data.behaviouralAnalysis.language && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="bg-white rounded-lg p-4 border border-gray-200"
                  >
                    <span className="font-semibold text-gray-700 text-sm block mb-2">Language</span>
                    <div className="text-gray-800 text-sm leading-relaxed">{data.behaviouralAnalysis.language}</div>
                  </motion.div>
                )}

                {data.behaviouralAnalysis.emotions && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="bg-white rounded-lg p-4 border border-gray-200"
                  >
                    <span className="font-semibold text-gray-700 text-sm block mb-2">Emotions</span>
                    <div className="text-gray-800 text-sm leading-relaxed">{data.behaviouralAnalysis.emotions}</div>
                  </motion.div>
                )}

                {data.behaviouralAnalysis.body && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    className="bg-white rounded-lg p-4 border border-gray-200"
                  >
                    <span className="font-semibold text-gray-700 text-sm block mb-2">Body Language</span>
                    <div className="text-gray-800 text-sm leading-relaxed">{data.behaviouralAnalysis.body}</div>
                  </motion.div>
                )}

                {data.behaviouralAnalysis.mindsets && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                    className="bg-white rounded-lg p-4 border border-gray-200"
                  >
                    <span className="font-semibold text-gray-700 text-sm block mb-2">Mindsets & Beliefs</span>
                    <div className="text-gray-800 text-sm leading-relaxed">{data.behaviouralAnalysis.mindsets}</div>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </div>

      {/* Enhanced Footer */}
      {!isGenerating && onClose && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8 }}
          className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end"
        >
          <Button
            onClick={onClose}
            className="bg-primary hover:bg-primary/90 text-white px-6 py-2 rounded-lg transition-colors"
          >
            Continue Your Journey
          </Button>
        </motion.div>
      )}
    </motion.div>
  );
}